import { useState } from 'react'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'
import { IconCalendar } from '@calendly/ui/components/glyphs/IconCalendar'
import { IconUserSquareRounded } from '@calendly/ui/components/glyphs/IconUserSquareRounded'
import { IconMail } from '@calendly/ui/components/glyphs/IconMail'
import { IconCreditCard } from '@calendly/ui/components/glyphs/IconCreditCard'
import { IconTag } from '@calendly/ui/components/glyphs/IconTag'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { Loader } from '@calendly/ui/components/loader'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { httpRequest } from '@calendly/http-tools'
import { Button } from '@calendly/ui/components/button'

import { useI18n } from 'locales'
import { useTrack } from 'hooks/useTrack'

import { usePurchasedPackage } from '../PackageContext'
import { useTrackPackagePageView } from '../../../hooks/useTrackPackagePageView'
import { getPackagePrice, getTotalSessionCount } from '../../../utils'
import { isPackagePaymentLink } from '../../../utils/package-helpers'

import {
  PageContainer,
  ModalOverlay,
  Modal,
  ModalContent,
  ModalTitle,
  ModalMessage,
  DetailRow,
  DetailIcon,
  DetailText,
  DetailLabel,
  SessionPackageRow,
  PackageNameText,
  ConfirmationText,
} from './styles'

const PackageSuccessPage = () => {
  const { profileSlug, purchasedPackageUuid } = useParams()
  const { i18n } = useI18n()
  const isPhone = useDeviceType('isPhone')

  const [schedulingSession, setSchedulingSession] = useState(false)
  const purchasedPackage = usePurchasedPackage()

  const sessionCount = getTotalSessionCount(
    purchasedPackage?.purchased_package_items,
  )
  const packagePrice = getPackagePrice(purchasedPackage)
  const isPaymentLink = isPackagePaymentLink(purchasedPackage)
  const track = useTrack()

  useTrackPackagePageView('package_purchase_complete', purchasedPackage)

  const handleScheduleSession = async () => {
    if (schedulingSession) return

    const purchasedPackageItems = purchasedPackage?.purchased_package_items
    if (!purchasedPackageItems || purchasedPackageItems.length === 0) return

    setSchedulingSession(true)

    track({
      name: 'CTA Clicked',
      dependencies: { user_uuid: purchasedPackage?.owner_uuid },
      properties: {
        cta_key: 'meeting_package_checkout_primary_button',
        cta_text: 'schedule_your_first_session',
        cta_destination: 'meeting_package_booking_flow',
        action: 'open_meeting_package_booking',
      }
    })

    // Navigate to the first available session booking page
    const eventTypeUuid = purchasedPackageItems[0].item_uuid

    try {
      // Fetch the event type by UUID to get the slug
      const [success, eventType] = await httpRequest({
        url: '/api/booking/event_types/lookup',
        method: 'GET',
        params: {
          event_type_uuid: eventTypeUuid,
        },
      })

      if (!success || !eventType?.slug) {
        setSchedulingSession(false)
        return
      }

      const eventTypeSlug = eventType.slug
      window.location.href = `/${profileSlug}/${eventTypeSlug}?redeem=${purchasedPackageUuid}`
    } catch (err) {
      console.error('Error fetching event type:', err)
      setSchedulingSession(false)
    }
  }

  if (!purchasedPackage) {
    return (
      <PageContainer>
        <ModalOverlay>
          <Modal className={classNames({ isPhone })}>
            <ModalContent>
              <Loader />
              <div>{i18n.t('booking.package_success.loading')}</div>
            </ModalContent>
          </Modal>
        </ModalOverlay>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <ModalOverlay>
        <Modal className={classNames({ isPhone })}>
          <ModalContent>
            <ModalTitle>{i18n.t('booking.package_success.title')}</ModalTitle>

            {purchasedPackage.confirmation_message && (
              <ModalMessage>
                {purchasedPackage.confirmation_message}
              </ModalMessage>
            )}

            {!isPaymentLink && (
              <SessionPackageRow>
                <DetailIcon>
                  <UiIcon icon={IconCalendar} size="small" />
                </DetailIcon>
                <div>
                  <DetailLabel>
                    {`${sessionCount}-${i18n.t('booking.package.session_package')}`}
                  </DetailLabel>
                  <PackageNameText>
                    {purchasedPackage.name || 'Package'}
                  </PackageNameText>
                </div>
              </SessionPackageRow>
            )}

            {isPaymentLink && (
              <SessionPackageRow>
                <DetailIcon>
                  <UiIcon icon={IconTag} size="small" />
                </DetailIcon>
                <div>
                  <DetailLabel>
                    {purchasedPackage.name || 'Package'}
                  </DetailLabel>
                </div>
              </SessionPackageRow>
            )}

            <DetailRow>
              <DetailIcon>
                <UiIcon icon={IconCreditCard} size="small" />
              </DetailIcon>
              <DetailText>
                {packagePrice}
                {' '}
                {i18n.t('booking.package_success.paid')}
              </DetailText>
            </DetailRow>

            <DetailRow>
              <DetailIcon>
                <UiIcon icon={IconUserSquareRounded} size="small" />
              </DetailIcon>
              <DetailText>
                {purchasedPackage.purchaser_name || 'Customer'}
              </DetailText>
            </DetailRow>

            <DetailRow>
              <DetailIcon>
                <UiIcon icon={IconMail} size="small" />
              </DetailIcon>
              <DetailText>{purchasedPackage.purchaser_email || ''}</DetailText>
            </DetailRow>

            {!isPaymentLink && (
              <ConfirmationText>
                {i18n.t('booking.package_success.confirmation_text')}
              </ConfirmationText>
            )}

            {!isPaymentLink && (
              <Button onClick={handleScheduleSession} loading={schedulingSession} disabled={schedulingSession}>
                {i18n.t('booking.package_success.schedule_button')}
              </Button>
            )}
          </ModalContent>
        </Modal>
      </ModalOverlay>
    </PageContainer>
  )
}

export default PackageSuccessPage
